import { useState } from "react";
import { Mail, Send, CheckCircle, AlertCircle } from "lucide-react";
import { track } from "../analytics";
import { useMetaTags } from "../components/MetaTags";

export function Contact() {
  useMetaTags({
    title: "Contact OmniCanvas — Questions, Feedback & Support",
    description:
      "Get in touch with the OmniCanvas team. Questions, suggestions, bug reports, or partnership ideas — we'd love to hear from you.",
    url: "https://omnicanvasnotes.com/contact",
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setStatusMessage("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setStatus("success");
      setStatusMessage(data.message || "Message sent! We'll get back to you soon.");
      track("contact_submit");
    } catch (err: any) {
      setStatus("error");
      setStatusMessage(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="pt-28 pb-20">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-sm font-medium px-4 py-1.5 rounded-full mb-6 border border-indigo-100">
            <Mail size={14} />
            Get in touch
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-slate-600">
            Have a question, suggestion, or just want to say hello? We'd love to hear from you.
          </p>
        </div>

        {status === "success" ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <CheckCircle size={40} className="text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Message sent!</h3>
            <p className="text-slate-600">{statusMessage}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
                Name
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">
                Message
              </label>
              <textarea
                id="message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white resize-none"
              />
            </div>

            {status === "error" && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle size={14} />
                <span>{statusMessage}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="group w-full bg-indigo-600 text-white font-semibold px-6 py-3.5 rounded-xl shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {status === "loading" ? "Sending..." : "Send Message"}
              <Send size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>
        )}

        <div className="mt-12 text-center text-slate-500 text-sm">
          <p>
            You can also reach us directly at{" "}
            <a
              href="mailto:hello@omnicanvasnotes.com"
              className="text-indigo-600 hover:text-indigo-700 underline"
            >
              hello@omnicanvasnotes.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
